import { AxiosResponse } from 'axios'
import { httpClient } from 'ServerAPI'

export type FileType = 'media' | 'project'

type ResponseBody = {
  id: string
}

type UploadSessionParams<T> = T extends 'media' ? {
  folder?: string,
} : {
  fps?: number,
  // eslint-disable-next-line camelcase
  createFolder?: boolean
}

type Body = {
  name?: string,
  size?: number,
  absPathName?: string
}

export function createUploadSession<T extends FileType>(body: Body, type: T, params: UploadSessionParams<T> = {}) {
  return httpClient.post<Body & UploadSessionParams<T>, AxiosResponse<ResponseBody>>(
    `/api/upload/${type}`, { ...body, ...params }
  )
}

type UploadFileRequestParams = RequestParams & {
  type: FileType
}

export function uploadFileRequest({ id,
  chunk, start, end,
  totalSize, type, controller = new AbortController() }: UploadFileRequestParams) {
  return httpClient.put(`/api/upload/${type}`, chunk, {
    signal: controller.signal,
    headers: {
      'Content-type': 'application/octet-stream',
      'Content-Range': `bytes ${start}-${end}/${totalSize}`,
    },
    params: {
      id,
    },
  })
}

export type RequestParams = {
  id: string,
  chunk: Blob,
  start: number,
  end: number,
  totalSize: number,
  controller?: AbortController
}

type UploadFileParams = {
  id: string,
  file: File & { path: string },
  request: (x: RequestParams) => Promise<AxiosResponse<unknown>>
  onFileUploaded?: () => void,
  onError?: (e?: Error) => void,
  controller?: AbortController
}

export const uploadFile = async ({
  id,
  file,
  request,
  onFileUploaded = () => {},
  onError = () => {},
  controller = new AbortController(),
  threads = 4, // 4 parts for parallel upload
}: UploadFileParams & { threads?: number }) => {
  const CHUNK_SIZE = 256 * 1024
  const totalSize = file.size
  const partSize = Math.ceil(totalSize / threads) // Split total size into 4 parts
  // let uploadedSize = 0
  let errorOccurred = false

  function cancel() {
    errorOccurred = true
    controller.signal.removeEventListener('abort', cancel)
  }

  controller.signal.addEventListener('abort', cancel)

  const uploadChunk = async (start: number, end: number) => {
    const chunk = file.slice(start, end)
    const { status } = await request({
      id,
      chunk,
      start,
      end: end - 1,
      totalSize,
      controller,
    })

    if (status !== 200 && status !== 206) {
      throw new Error(`Failed to upload chunk from ${start} to ${end}`)
    }

    // uploadedSize += (end - start)
    // const progress = (uploadedSize / totalSize) * 100
    // console.log(`Progress: ${progress.toFixed(2)}%`)
  }

  const uploadPart = async (partIndex: number) => {
    const start = partIndex * partSize
    const end = Math.min((partIndex + 1) * partSize, totalSize)

    // Now upload the chunks sequentially within the part
    let chunkStart = start
    while (chunkStart < end && !errorOccurred) {
      const chunkEnd = Math.min(chunkStart + CHUNK_SIZE, end)
      try {
        // eslint-disable-next-line
        await uploadChunk(chunkStart, chunkEnd)
        chunkStart = chunkEnd // Move to the next chunk
      } catch (err) {
        onError()
        errorOccurred = true
        controller.abort()
      }
    }
  }

  // Upload parts in parallel (4 parts)
  const uploadPartsInParallel = async () => {
    const uploadPromises = []

    for (let partIndex = 0; partIndex < threads; partIndex++) {
      if (errorOccurred) break
      uploadPromises.push(uploadPart(partIndex))
    }

    try {
      await Promise.all(uploadPromises)
    } catch (err) {
      onError()
      errorOccurred = true
    }
  }

  // Start the parallel uploads
  await uploadPartsInParallel()

  if (!errorOccurred) {
    onFileUploaded()
  }
}


export const cancelImport = async (url: string) => httpClient.delete(url)

export type ProjectUploadStage = 'upload' | 'unpack' | 'import_media' | 'ready' | 'error'

type ProgressResponseBody = {
    status: ProjectUploadStage,
    progress: number,
    error: string,
}

export const getProjectProgress = async (id: string, controller = new AbortController()) => {
  const { data } = await httpClient.get<ProgressResponseBody>(`/api/upload/project?id=${id}`, {
    signal: controller.signal,
  })

  if (data.error) {
    throw Error(data.error)
  }

  return { progress: data.progress, stage: data.status }
}
